import React, {useState} from 'react';
import PropTypes from 'prop-types';
import {useIntl} from 'react-intl';
import {APPROVAL_TYPE} from '../../../constants';
import {createToast} from '../../../forecast-app/shared/components/toasts/another-toast/toaster';
import Util from '../../../forecast-app/shared/util/util';
import UpdateApprovalStatusMutation from '../../../mutations/UpdateApprovalStatusMutation';
import {showApprovalErrorModal} from '../../../components/timesheets/time-approval/ApprovalErrors';
import {trackEvent} from '../../../tracking/amplitude/TrackingV2';
import {FlexColumn, Modal, Text, DatePicker} from '@forecast-it/design-system';
import moment from 'moment';

const SubmitTimeApprovalModal = ({
	selectedPersonId,
	selectedPersonName,
	currentViewingDate,
	usingTimeOffApproval,
	lockedDate,
	hasApprovableTimeInWeek = true,
	closeModal,
}) => {
	const intl = useIntl();
	const {formatMessage} = intl;
	const startDateLimit = lockedDate && lockedDate.clone().add(1, 'd');
	const currentDateNoLock =
		lockedDate && lockedDate.isSameOrAfter(currentViewingDate, 'day') ? startDateLimit : currentViewingDate;

	const [selectedDate, setSelectedDate] = useState(currentDateNoLock);

	const handleConfirmButton = () => {
		trackEvent('Submit Timesheet Modal Save Button', 'Clicked');
		const onSuccess = res => {
			// A refusal comes back in `errors` with `persons` null, so it has to be checked
			// before the success fields. closeOtherModals replaces this modal — a warning stacked
			// behind it cannot be read, and the submit has to be re-driven either way.
			if (showApprovalErrorModal(res.updateApprovalStatus?.errors, true)) return;
			const personRes = res.updateApprovalStatus?.persons?.[0];
			if (personRes) {
				const newPersonLockDate = Util.CreateNonUtcMomentDate(
					personRes.submitLockedDateYear,
					personRes.submitLockedDateMonth,
					personRes.submitLockedDateDay
				);
				createToast({
					duration: 2500,
					message: `Timesheets submitted until ${newPersonLockDate.clone().format('LL')}`,
				});
				closeModal();
			} else {
				// Applied, but the payload carried no person to read a new lock date from, so there is
				// no toast to show. Close anyway rather than leaving the modal hanging open.
				closeModal();
			}
		};

		// Mutation
		Util.CommitMutation(
			UpdateApprovalStatusMutation,
			{
				personIds: [selectedPersonId],
				approvalType: APPROVAL_TYPE.SUBMIT,
				endYear: selectedDate.year(),
				endMonth: selectedDate.month() + 1,
				endDay: selectedDate.date(),
				includeTimeOff: true,
			},
			onSuccess
		);
	};

	const cancelButton = () => {
		trackEvent('Submit Timesheet Modal Cancel Button', 'Clicked');
		closeModal();
	};

	const handleDateRangeChanged = newDate => {
		trackEvent('Submit Timesheet Modal Submit Until Date', 'Changed', {
			oldDate: selectedDate ? selectedDate.format('YYYY-MM-DD') : 'No date',
			newDate: newDate ? newDate : 'No date',
		});
		setSelectedDate(newDate ? moment(newDate) : undefined);
	};

	const messageIdSuffix = (usingTimeOffApproval ? '_and_time_off' : '') + '_info';

	// When cleaning up this FF, also clean up the use of Moment in this component.

	const content = (
		<FlexColumn gap="m">
			<Text>
				{selectedPersonName
					? formatMessage({id: 'time_approval.submit_names_timesheets' + messageIdSuffix}, {name: selectedPersonName})
					: formatMessage({id: 'time_approval.submit_timesheets' + messageIdSuffix})}
			</Text>
			{/*
			 * PIKA-740 criterion 11. Where the week holds nothing an approver has to act on, this
			 * button does not send anything to anyone: the submit matches no rows in the week and
			 * what makes it read as submitted is the submission lock advancing. That is a different
			 * act from the one the label promises, so it is said before the person commits rather
			 * than left to be inferred from a week that quietly went quiet.
			 *
			 * Scoped to the week on purpose. The submit itself is a sweep up to the chosen date, so
			 * the same press may still send real time from earlier weeks — the notice says what is
			 * true of the week in view and does not claim the action as a whole changes nothing.
			 */}
			{!hasApprovableTimeInWeek && (
				<Text>{formatMessage({id: 'time_approval.submit_no_time_requiring_approval_notice'})}</Text>
			)}
			<DatePicker
				label={formatMessage({id: 'time_approval.submit_timesheets_until'})}
				onChange={handleDateRangeChanged.bind(this)}
				minValue={startDateLimit?.format('YYYY-MM-DD')}
				value={selectedDate?.format('YYYY-MM-DD')}
			/>
		</FlexColumn>
	);

	return (
		<Modal size={Modal.SIZE.MEDIUM} onCloseModal={closeModal}>
			<Modal.Title>
				{selectedPersonName
					? formatMessage({id: 'time_approval.submit_names_timesheets'}, {name: selectedPersonName})
					: formatMessage({id: 'time_approval.submit_timesheets'})}
			</Modal.Title>
			<Modal.Content>{content}</Modal.Content>
			<Modal.Footer>
				<Modal.PrimaryFooterButton
					onPress={handleConfirmButton.bind(this)}
					dataCy="submit-time-save-button"
					disabled={!selectedDate}
				>
					{formatMessage({id: 'common.save'})}
				</Modal.PrimaryFooterButton>
				<Modal.SecondaryFooterButton onPress={cancelButton.bind(this)}>
					{formatMessage({id: 'common.cancel'})}
				</Modal.SecondaryFooterButton>
			</Modal.Footer>
		</Modal>
	);
};

SubmitTimeApprovalModal.propTypes = {
	hasApprovableTimeInWeek: PropTypes.bool,
	currentViewingDate: PropTypes.object,
	closeModal: PropTypes.func.isRequired,
};

export default SubmitTimeApprovalModal;
